import React from 'react';

const facilities = [
  {
    name: 'コワーキングフロア（4F）',
    description:
      '目的に合わせてエリアを使い分けられるフリーアドレスのコワーキングフロア。集中エリアやラウンジエリアなど、気分に合わせて席を選べます。MeVER Chairを設置しており、実際の作業環境で座り心地を体験できます。',
    image:
      'https://public.readdy.ai/ai/img_res/804ce9290af7d7fee2b06fe8014f9697.jpg',
    features: ['MeVER Chair 設置', 'ほぼ全席に電源', 'フリーアドレス'],
  },
  {
    name: 'フードホール（5F）',
    description:
      '5Fのフードホールフロアも出入り自由。ハンドドリップコーヒーやグルメを楽しみながら仕事ができます。4Fへのお食事の持ち込みも可能です。',
    image:
      'https://readdy.ai/api/search-image?query=modern%20Japanese%20food%20hall%20interior%20with%20wooden%20tables%20comfortable%20chairs%20coffee%20counter%20warm%20ambient%20lighting%20green%20plants%20minimalist%20design%20coworking%20cafe%20dining%20space%20professional%20architectural%20photography&width=700&height=500&seq=favy-cowork-fac-004&orientation=landscape',
    features: ['ドリンク・フード', '4Fへ持ち込み可', 'リラックス空間'],
  },
  {
    name: '会議室（4室）',
    description:
      '4つの会議室を完備。昼は会議室として、夜は個室レストランとしても利用可能。プロジェクターやホワイトボードを備え、チームミーティングに最適です。',
    image:
      'https://readdy.ai/api/search-image?query=modern%20Japanese%20meeting%20room%20with%20glass%20walls%20long%20wooden%20table%20ergonomic%20chairs%20projector%20screen%20whiteboard%20minimalist%20design%20warm%20natural%20lighting%20professional%20business%20space%20architectural%20photography&width=700&height=500&seq=favy-cowork-fac-003&orientation=landscape',
    features: ['1時間から予約可', '1,500円/時', 'ゲスト招待可'],
  },
  {
    name: '個人ブース（9室）',
    description:
      '電話やWeb会議に使える防音個人ブースを9室設置。30分から予約可能で、オンラインミーティングも周囲を気にせず快適に行えます。',
    image:
      'https://readdy.ai/api/search-image?query=modern%20soundproof%20phone%20booth%20pod%20in%20Japanese%20coworking%20space%20glass%20walls%20single%20wooden%20desk%20ergonomic%20chair%20warm%20lighting%20minimalist%20design%20clean%20compact%20interior%20professional%20architectural%20photography&width=700&height=500&seq=favy-cowork-fac-002&orientation=landscape',
    features: ['30分から予約可', '275円/30分', '防音仕様'],
  },
];

export default function CoworkingFacilities() {
  /**
   * Handles image loading errors by replacing the broken image with a fallback placeholder.
   * @param {React.SyntheticEvent<HTMLImageElement, Event>} e
   */
  const handleImageError = (e) => {
    e.currentTarget.onerror = null; // Prevent infinite loop in case placeholder also fails
    e.currentTarget.src =
      'data:image/svg+xml;charset=UTF-8,<svg%20width%3D"700"%20height%3D"500"%20xmlns%3D"http://www.w3.org/2000/svg"><rect%20width%3D"700"%20height%3D"500"%20fill%3D"%23e0e0e0"/><text%20x%3D"50%"%20y%3D"50%"%20dominant-baseline%3D"middle"%20text-anchor%3D"middle"%20font-size%3D"24"%20fill%3D"%23666">Image%20unavailable</text></svg>';
  };

  return (
    <section
      className="py-16 sm:py-24 md:py-32 bg-[#FAF8F5]"
      id="facilities"
      aria-labelledby="facilities-heading"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <div className="text-center mb-12 sm:mb-16 md:mb-20">
          <span className="inline-block px-3 py-1.5 bg-emerald-500/10 text-emerald-600 text-xs tracking-[0.15em] rounded-full mb-4 sm:mb-6">
            FACILITIES
          </span>
          <h2
            id="facilities-heading"
            className="text-3xl sm:text-4xl md:text-5xl font-extralight text-black mb-3 sm:mb-4 tracking-[0.1em]"
          >
            充実した施設
          </h2>
          <p className="text-sm sm:text-base text-black/60 font-extralight tracking-wide">
            アドホック新宿ビル 4F・5Fの2フロアで快適なワークスペースを提供
          </p>
        </div>

        <div className="space-y-8 sm:space-y-12">
          {facilities.map((facility, index) => (
            <article
              key={index}
              className="grid lg:grid-cols-2 gap-6 sm:gap-10 items-center"
            >
              {/* Image column */}
              <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                <div className="aspect-[7/5] rounded-xl sm:rounded-2xl overflow-hidden shadow-md">
                  <img
                    src={facility.image}
                    alt={facility.name}
                    className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                    width="700"
                    height="500"
                    onError={handleImageError}
                  />
                </div>
              </div>

              {/* Text column */}
              <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                <h3 className="text-xl sm:text-2xl md:text-3xl font-extralight text-black mb-3 sm:mb-4 tracking-wide">
                  {facility.name}
                </h3>
                <p className="text-sm sm:text-base text-black/60 font-extralight leading-relaxed mb-5 sm:mb-6 tracking-wide">
                  {facility.description}
                </p>
                <div className="flex flex-wrap gap-2 sm:gap-3">
                  {facility.features.map((feature, idx) => (
                    <span
                      key={idx}
                      className="inline-flex items-center gap-1.5 px-3 sm:px-4 py-1.5 sm:py-2 bg-white rounded-lg text-xs sm:text-sm text-black/70 font-extralight tracking-wide shadow-sm"
                    >
                      <i
                        className="ri-check-line text-emerald-500 text-xs"
                        aria-hidden="true"
                      ></i>
                      {feature}
                    </span>
                  ))}
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
